import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { RouteService } from '@/support/RouteService';
import { FC, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RiAlertFill } from 'react-icons/ri';
import { DateUtils } from '@/utils/Date.utils';
import {
  entityTypeToStringMap,
  errorTypeToStringMap,
  statusToStringMap,
  syncEventFilterOptions,
  benchlingSyncEventCustomFilterProcessor,
  SYNC_STATUS_FILTER_CONFIG,
} from '@/model/BenchlingBackfill.model';
import { modalAction } from '@/utils/modal';
import { successToast } from '@/helpers';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { components } from '@/generated/internal-api/openapi-types';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { Study } from '@/model/Study.model';
import { isWriteUserForStudy } from '@/support/auth';
import Truncate from '@/components/UI/Truncate/Truncate';
import BenchlingSyncStatusAside from '@/components/UI/BenchlingSyncStatusAside/BenchlingSyncStatusAside';
import { _notNil } from '@/littledash.ts';
import {
  StudySyncEventsSyncEntityRequestProps,
  SyncEntityContext,
} from '@/components/Modals/SyncEntityDetails/SyncEntityDetails.utils';

type BenchlingSyncEventV1 = components['schemas']['BenchlingSyncEventV1.schema'];

const BenchlingSync: FC = () => {
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const { id: studyId } = useParams();
  const [syncEventsMeta, setSyncEventsMeta] = useState<Record<string, any> | null>(null);

  // Get study data for permissions check
  const { entity: study } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
    includes: 'users',
  });

  // Check user permissions for write access
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hasWriteAccess = study ? isWriteUserForStudy(study) : false;

  const settings = useSelector<{ ui: { settings: Record<string, any> } }>((state) => state.ui.settings);

  const { invoke: retrySyncEvents } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/sync-events',
    invokeOnInit: false,
  });

  // Handler for when table data is fetched to capture error count
  const handleSyncEventsDataChange = (_data: BenchlingSyncEventV1[], additionalProps?: Record<string, any>) => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    setSyncEventsMeta(additionalProps || null);
  };

  // Create the error count AsideComponent
  const ErrorCountAsideComponent = ({ useTableProps }: { useTableProps: any }) => {
    const errorCount = syncEventsMeta?.error_count ?? 0;

    return (
      <BenchlingSyncStatusAside
        errorCount={errorCount}
        filterConfig={SYNC_STATUS_FILTER_CONFIG}
        useTableProps={useTableProps}
      />
    );
  };

  const handleRetrySyncEvents = async (eventApiIds: Array<string>, fetchTableData: () => void) => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!study?.api_id) {
      return;
    }

    try {
      await retrySyncEvents({
        path: { studyApiId: study.api_id },
        body: { events: eventApiIds },
      });

      // Always refresh table data
      fetchTableData();

      // Show success toast after table refresh
      successToast(`Retrying ${eventApiIds.length} sync event${eventApiIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      // Always refresh table data even on error
      fetchTableData();

      // Error handling is managed by the API hook
    }
  };

  const closeModal = useCallback(() => {
    dispatch({ type: 'CLOSE_MODAL' });
  }, [dispatch]);

  // Extract and format event type from camelCase to readable format
  const formatEventType = (event: string): string => {
    // Convert camelCase to separate words and capitalize first letter
    const match = event.match(/([A-Z][a-z]+|[a-z]+)/g);
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (match && match.length > 0) {
      // Take the last word (the action) and capitalize it
      const action = match[match.length - 1];
      return action.charAt(0).toUpperCase() + action.slice(1);
    }
    return event;
  };

  const activityBulkActions: (props: UseTableProps<BenchlingSyncEventV1>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { fetchTableData },
    },
  }) => {
    const eventApiIds = selectedFlatRows.map(({ original: { api_id } }) => api_id);
    const isDisabled: boolean = selectedFlatRows.some(
      ({ original }) => original.status !== 'failed' && original.status !== 'deactivated'
    );

    return [
      {
        name: `Retry sync event${selectedFlatRows?.length > 1 ? 's' : ''}`,
        disabled: isDisabled || !hasWriteAccess,
        tooltip: !hasWriteAccess
          ? 'Write access required to retry sync events'
          : isDisabled
            ? 'Only failed events can be retried'
            : undefined,
        action: () =>
          openModal('CONFIRM_RETRY_SYNC_EVENTS', {
            onClick: () => handleRetrySyncEvents(eventApiIds, fetchTableData),
          }),
      },
    ];
  };

  const activityTableColumns: Array<TanStackColumnDef<BenchlingSyncEventV1>> = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: true,
        isVisible: true,
        size: 120,
        cell: ({ row: { original } }: { row: { original: BenchlingSyncEventV1 } }) => (
          <div className="flex items-center justify-between w-100">
            {statusToStringMap[original.status]}
            {original.status === 'failed' || original.status === 'deactivated' ? <RiAlertFill className="red" /> : null}
          </div>
        ),
      },
      {
        id: 'updated_at',
        header: 'Updated at',
        accessorKey: 'updated_at',
        enableSorting: true,
        isVisible: true,
        size: 140,
        cell: ({ row: { original } }: { row: { original: BenchlingSyncEventV1 } }) => (
          <>{DateUtils.renderDateTime(original.updated_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'entity_type',
        header: 'Item type',
        accessorKey: 'entity_type',
        enableSorting: true,
        isVisible: true,
        size: 120,
        cell: ({ row: { original } }: { row: { original: BenchlingSyncEventV1 } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          entityTypeToStringMap?.[original.entity_type] ? (
            <div
              className="link"
              onClick={() =>
                openModal('SYNC_ENTITY_DETAILS', {
                  syncEntityRequestProps: {
                    context: SyncEntityContext.STUDY_SYNC_EVENTS,
                    studyApiId: study?.api_id,
                    syncEventApiId: original.api_id,
                  } as StudySyncEventsSyncEntityRequestProps,
                  entityType: original.entity_type,
                })
              }
            >
              {entityTypeToStringMap?.[original.entity_type]}
            </div>
          ) : (
            '-'
          ),
      },
      {
        id: 'entity_api_id',
        header: 'Item ID',
        accessorKey: 'entity_api_id',
        enableSorting: false,
        isVisible: false,
        size: 200,
        cell: ({ row: { original } }: { row: { original: BenchlingSyncEventV1 } }) => (
          <Truncate tooltip={original.entity_api_id}>{original.entity_api_id}</Truncate>
        ),
      },
      {
        id: 'event_type',
        header: 'Event type',
        accessorKey: 'event',
        enableSorting: true,
        isVisible: true,
        size: 120,
        cell: ({ row: { original } }: { row: { original: BenchlingSyncEventV1 } }) => (
          <>{formatEventType(original.event)}</>
        ),
      },
      {
        id: 'error_type',
        header: 'Error message',
        accessorKey: 'error_type',
        enableSorting: false,
        isVisible: true,
        size: 200,
        cell: ({ row: { original } }: { row: { original: BenchlingSyncEventV1 } }) => (
          <>
            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
            {original.error ? (
              <button
                className="link-button"
                onClick={() => openModal('VIEW_EVENT_ERRORS', { error: original.error, closeModal })}
                data-testid="view-errors-button"
              >
                {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
                {original.error_type ? errorTypeToStringMap[original.error_type] : 'View error'}
              </button>
            ) : (
              '-'
            )}
          </>
        ),
      },
    ],
    [openModal, closeModal, study]
  );

  return (
    <div className="pv3 ph4">
      {_notNil(study?.api_id) && (
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/studies/{studyApiId}/sync-events',
              path: { studyApiId: study?.api_id as components['schemas']['StudyApiId.schema'] },
            }).url,
          }}
          columns={activityTableColumns}
          bulkActions={activityBulkActions}
          filterOptions={syncEventFilterOptions}
          customFilterProcessor={benchlingSyncEventCustomFilterProcessor}
          defaultSortBy={{ id: 'updated_at', desc: true }}
          settings={settings}
          reduxTableName="studySyncEvents"
          hideSearch
          testIdPrefix="study-sync-events"
          onFetchTableData={handleSyncEventsDataChange}
          AsideComponent={ErrorCountAsideComponent}
        />
      )}
    </div>
  );
};

export default BenchlingSync;
